import React from 'react';
import { createPortal } from 'react-dom';

import {
  Body,
  CloseButton,
  Footer,
  Header,
  ModalSize,
  Overlay,
  Panel,
  Title,
} from './styles';

export type ModalProps = {
  open: boolean;
  title?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: ModalSize;
  onClose?: () => void;
  closeOnOverlayClick?: boolean;
  className?: string;
};

const CloseIcon = (): JSX.Element => (
  <svg viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M4.5 4.5L13.5 13.5M13.5 4.5L4.5 13.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const Modal = ({
  open,
  title,
  children,
  footer,
  size = 'md',
  onClose,
  closeOnOverlayClick = true,
  className,
}: ModalProps): JSX.Element | null => {
  if (!open) return null;

  const handleOverlayClick = (): void => {
    if (closeOnOverlayClick) onClose?.();
  };

  const content = (
    <Overlay
      role="presentation"
      onClick={handleOverlayClick}
      className={className}
    >
      <Panel
        $size={size}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        onClick={(event) => event.stopPropagation()}
      >
        {(title || onClose) && (
          <Header>
            {title ? <Title>{title}</Title> : <span />}
            {onClose && (
              <CloseButton type="button" aria-label="Fechar" onClick={onClose}>
                <CloseIcon />
              </CloseButton>
            )}
          </Header>
        )}

        <Body>{children}</Body>

        {footer && <Footer>{footer}</Footer>}
      </Panel>
    </Overlay>
  );

  if (typeof document === 'undefined') return content;

  return createPortal(content, document.body);
};

export default Modal;
